/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
// @flow
import type { Node } from 'react';

import React, { useState } from 'react';
// eslint-disable-next-line import/extensions
import { FaInfoCircle } from 'react-icons/fa';
import { Button, Popover, PopoverBody, PopoverHeader } from 'reactstrap';
import { icon, snippet, viewPageNote } from './ProvenancePopover.module.scss';

type Props = {|
  provenance: ?{|
    // The citation (sourceSnippet/sourcePage) is present only for `stated` values; an
    // inferred/assumed value (e.g. a geocoded coordinate, #196) carries none, so both are optional.
    sourceSnippet?: string,
    sourcePage?: number,
    evidenceBasis: 'stated' | 'inferred' | 'assumed',
    rationale: string,
    documentS3Key: string,
  |},
  fieldId: string,
  onViewCitedPage: ?Function,
|};

const EVIDENCE_BASIS_LABEL = {
  stated: 'Stated',
  inferred: 'Inferred',
  assumed: 'Assumed',
};

// The two documented `document`-endpoint failures (UI design §3.3): FORBIDDEN (the model
// the key belongs to left the caller's org) reads as "not permitted", NOT_FOUND (the
// retained PDF is gone -- the future retention-purge case) reads as "no longer available",
// so the two causes aren't confused.
const VIEW_PAGE_ERROR_MESSAGE = {
  FORBIDDEN: 'You no longer have access to view the cited page.',
  NOT_FOUND: 'The cited page is no longer available.',
};

// Any rejection whose responseBody.error isn't one of the two documented codes above --
// a bare network failure (offline/CORS/DNS, no responseBody at all) or an unrecognized
// future error code -- still needs a user-visible message rather than silently
// re-enabling the button with no explanation of what happened.
const GENERIC_VIEW_PAGE_ERROR_MESSAGE = 'Something went wrong opening the cited page.';

/*
 * The provenance popover (UI design §3.3): an info icon that, on click (or Enter/Space
 * while focused, so it's reachable without a mouse), pins open the verbatim
 * sourceSnippet/sourcePage/rationale plus a "view the cited page" affordance. It's
 * click-to-pin rather than hover-triggered because the popover now holds a real
 * interactive control -- a hover-only popover the user can't move their mouse into
 * would make that control unreachable (§3.3's caveat).
 */
const ProvenancePopover = ({ provenance, fieldId, onViewCitedPage }: Props): Node => {
  const [isOpen, setIsOpen] = useState(false);
  const [documentState, setDocumentState] = useState({ status: 'idle' });

  if (!provenance) {
    return null;
  }
  const { sourceSnippet, sourcePage, evidenceBasis, rationale } = provenance;
  const targetId = `${fieldId.replace(/[^a-zA-Z0-9]/g, '_')}ProvenanceTarget`;

  // Reopening the popover always resets a prior error, so each reopen is a fresh attempt
  // (FORBIDDEN/NOT_FOUND aren't necessarily permanent -- org membership can be restored,
  // and a first hit could have been a transient upstream misclassification) -- `documentState`
  // otherwise persists for the lifetime of the field's row, since `Popover`'s `isOpen` only
  // toggles visibility rather than unmounting this component.
  const toggle = () => {
    setIsOpen(open => {
      const next = !open;
      if (next) {
        setDocumentState({ status: 'idle' });
      }
      return next;
    });
  };

  const handleIconKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggle();
    }
  };

  const handleViewCitedPage = () => {
    // The container supplies `onViewCitedPage` only when there IS provenance, and this component
    // has already returned null in that case -- so this is unreachable in practice. The guard is
    // what lets the prop stay honestly optional instead of being typed as always-present.
    // `sourcePage == null` is likewise unreachable (the button is only rendered when a page
    // exists, #196), but the guard makes that invariant explicit and refines sourcePage to a
    // number for the `#page=` navigation below.
    if (!onViewCitedPage || sourcePage == null) return;

    setDocumentState({ status: 'loading' });
    // Open a blank tab SYNCHRONOUSLY, in the same tick as the click, then navigate it once
    // the presigned URL resolves. Browsers gate window.open on user-activation that a real
    // network round trip (the fetch below) has already consumed by the time `.then` runs --
    // opening it there gets silently blocked as a popup in every mainstream browser.
    const newTab = window.open('', '_blank');
    onViewCitedPage()
      .then(({ payload }) => {
        if (newTab) {
          newTab.location = `${payload.url}#page=${sourcePage}`;
        }
        setDocumentState({ status: 'idle' });
      })
      .catch(rejectedAction => {
        if (newTab) {
          newTab.close();
        }
        const code = rejectedAction?.payload?.responseBody?.error;
        const message = VIEW_PAGE_ERROR_MESSAGE[code] || GENERIC_VIEW_PAGE_ERROR_MESSAGE;
        setDocumentState({ status: 'error', message });
      });
  };

  return (
    <>
      <FaInfoCircle
        size="0.85rem"
        className={icon}
        id={targetId}
        tabIndex="0"
        role="button"
        onKeyDown={handleIconKeyDown}
      />
      <Popover trigger="legacy" isOpen={isOpen} toggle={toggle} placement="top" target={targetId}>
        <PopoverHeader>
          {EVIDENCE_BASIS_LABEL[evidenceBasis] || evidenceBasis}
          {/* No page for an inferred/assumed value (e.g. a geocoded coordinate, #196). */}
          {sourcePage != null ? <> &middot; p.{sourcePage}</> : null}
        </PopoverHeader>
        <PopoverBody>
          <p>{rationale}</p>
          {/* A verbatim quote only exists for a `stated` value; derived values have none (#196). */}
          {sourceSnippet ? (
            <blockquote className={snippet}>&ldquo;{sourceSnippet}&rdquo;</blockquote>
          ) : null}
          {/* "View the cited page" needs a page to point at -- omit it when there is none (#196). */}
          {sourcePage != null &&
            (documentState.status === 'error' ? (
              <>
                <Button color="link" size="sm" className="p-0" disabled>
                  View the cited page
                </Button>
                <div className={viewPageNote}>{documentState.message}</div>
              </>
            ) : (
              <Button
                color="link"
                size="sm"
                className="p-0"
                disabled={documentState.status === 'loading'}
                onClick={handleViewCitedPage}
              >
                {documentState.status === 'loading' ? 'Opening…' : 'View the cited page'}
              </Button>
            ))}
        </PopoverBody>
      </Popover>
    </>
  );
};

export default ProvenancePopover;
